import {useState, useCallback, useEffect} from 'react';
import {defineMessages} from 'react-intl';
import {
  FileUploadValidationError,
  useFileUploader,
} from '../FileUploaderProvider';
import {useAnalytics} from '../AnalyticsProvider';

export type StagedFile = {
  file: File;
  uploadToken?: string;
  status: 'uploading' | 'uploaded';
};

export type FileError = {
  code: string;
};

const MAX_STAGED_FILES = 1; // limit files per message to 1 for initial rollout
const MAX_IMAGE_FILE_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB
const MAX_DOCUMENT_FILE_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB
const MAX_FILE_NAME_LENGTH = 200;

export const supportedImageMimeTypes: string[] = [
  'image/jpeg',
  'image/png',
  'image/webp',
];

const supportedDocumentMimeTypes: string[] = ['application/pdf'];

export const fileStagingMessages = defineMessages({
  uploadTypeNotSupported: {
    id: 'conversations_ui.fileStaging.uploadTypeNotSupported',
    description:
      'Error when the user uploads a file type that is not supported for staging.',
    defaultMessage:
      'File type is not supported, please upload a jpeg, webp, png or pdf file',
  },
  imageFileTooLarge: {
    id: 'conversations_ui.fileStaging.imageFileTooLarge',
    description:
      'Error when the user uploads an image that exceeds the maximum allowed size.',
    defaultMessage:
      'Image file is too large, please upload a file smaller than 20 MB',
  },
  documentFileTooLarge: {
    id: 'conversations_ui.fileStaging.documentFileTooLarge',
    description:
      'Error when the user uploads a document that exceeds the maximum allowed size.',
    defaultMessage:
      'Document file is too large, please upload a file smaller than 2 MB',
  },
  fileNameTooLong: {
    id: 'conversations_ui.fileStaging.fileNameTooLong',
    description:
      'Error when the user uploads a file whose name exceeds the character limit.',
    defaultMessage: 'File name must be 200 characters or less',
  },
  tooManyFiles: {
    id: 'conversations_ui.fileStaging.tooManyFiles',
    description:
      'Error when the user tries to stage more files than the allowed maximum.',
    defaultMessage: 'Only a single file can be uploaded at a time.',
  },
  unexpectedError: {
    id: 'conversations_ui.fileStaging.unexpectedError',
    description:
      'Fallback error when an unknown error code is encountered during file staging.',
    defaultMessage: 'File could not be uploaded, please try again later',
  },
});

export const errorCodeToMessage: Record<
  string,
  (typeof fileStagingMessages)[keyof typeof fileStagingMessages]
> = {
  upload_type_not_supported: fileStagingMessages.uploadTypeNotSupported,
  image_file_too_large: fileStagingMessages.imageFileTooLarge,
  document_file_too_large: fileStagingMessages.documentFileTooLarge,
  file_name_too_long: fileStagingMessages.fileNameTooLong,
  too_many_files: fileStagingMessages.tooManyFiles,
  unexpected_error: fileStagingMessages.unexpectedError,
};

const validateFile = (file: File) => {
  if (
    !supportedImageMimeTypes.includes(file.type) &&
    !supportedDocumentMimeTypes.includes(file.type)
  ) {
    throw new FileUploadValidationError({
      message: 'File type is not supported',
      file,
      code: 'upload_type_not_supported',
    });
  }

  if (supportedImageMimeTypes.includes(file.type)) {
    if (file.size > MAX_IMAGE_FILE_SIZE_BYTES) {
      throw new FileUploadValidationError({
        message: 'Image file is too large',
        file,
        code: 'image_file_too_large',
      });
    }
  }

  if (supportedDocumentMimeTypes.includes(file.type)) {
    if (file.size > MAX_DOCUMENT_FILE_SIZE_BYTES) {
      throw new FileUploadValidationError({
        message: 'Document file is too large',
        file,
        code: 'document_file_too_large',
      });
    }
  }

  if (file.name.length > MAX_FILE_NAME_LENGTH) {
    throw new FileUploadValidationError({
      message: 'File name is too long',
      file,
      code: 'file_name_too_long',
    });
  }
};

export function useFileStaging(
  purpose?: 'universal_help_attachment' | 'support_attachment',
) {
  const analytics = useAnalytics();
  const fileUploader = useFileUploader();
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [uploadErrors, setUploadErrors] = useState<FileError[]>([]);
  const trackUniversalHelpAttachmentAction = useCallback(
    (
      event:
        | 'universal_help_attachment_staged'
        | 'universal_help_attachment_removed',
      properties?: Record<string, unknown>,
    ) => {
      try {
        analytics.action(event, properties);
      } catch {}
    },
    [analytics],
  );

  const stageFile = useCallback(
    async (file: File) => {
      if (stagedFiles.length >= MAX_STAGED_FILES) {
        setUploadErrors([{code: 'too_many_files'}]);
        return;
      }

      try {
        validateFile(file);
      } catch (e) {
        if (e instanceof FileUploadValidationError) {
          setUploadErrors([{code: e.code ?? 'unexpected_error'}]);
          return;
        }
        throw e;
      }

      const entry: StagedFile = {file, status: 'uploading'};
      setStagedFiles((prev) => [...prev, entry]);

      if (fileUploader.available) {
        try {
          const uploadToken = await fileUploader.upload(file, {}, purpose);
          setStagedFiles((prev) =>
            prev.map((f) =>
              f.file === file ? {...f, uploadToken, status: 'uploaded'} : f,
            ),
          );
          if (purpose === 'universal_help_attachment') {
            trackUniversalHelpAttachmentAction(
              'universal_help_attachment_staged',
              {
                mimeType: file.type,
                purpose,
              },
            );
          }
        } catch {
          setStagedFiles((prev) => prev.filter((f) => f.file !== file));
          setUploadErrors([{code: 'unexpected_error'}]);
        }
      } else {
        setStagedFiles((prev) => prev.filter((f) => f.file !== file));
        setUploadErrors([{code: 'unexpected_error'}]);
      }
    },
    [
      fileUploader,
      purpose,
      stagedFiles.length,
      trackUniversalHelpAttachmentAction,
    ],
  );

  const unstageFile = useCallback(
    (index: number) => {
      setStagedFiles((prev) => {
        const fileToRemove = prev[index];
        if (fileToRemove && purpose === 'universal_help_attachment') {
          trackUniversalHelpAttachmentAction(
            'universal_help_attachment_removed',
            {purpose},
          );
        }
        return prev.filter((_, i) => i !== index);
      });
    },
    [purpose, trackUniversalHelpAttachmentAction],
  );

  useEffect(() => {
    if (uploadErrors.length === 0) return;
    const timer = setTimeout(() => setUploadErrors([]), 3000);
    return () => clearTimeout(timer);
  }, [uploadErrors]);

  const clearStaged = useCallback(() => {
    setStagedFiles([]);
  }, []);

  return {
    stagedFiles,
    stageFile,
    unstageFile,
    clearStaged,
    uploadErrors,
  };
}
